/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef } from "react";
import dynamic from "next/dynamic";
import { ClickOffer, ListingItem, PopupStyle } from "@lib/shared/types";
import { DomainInfo } from "src/api/domainInfo";
import { affiliateOfferZones } from "@lib/shared/config";
import { doesOfferHaveALink } from "@lib/sharedUtils";
import OfferBlock from "@components/shared/offer";
import InsuranceOffer from "@components/shared/insuranceOffer";
import { RegulationsContent } from "@components/shared/regulationsContent/RegulationsContent";
import styles from "@components/shared/form/styles.module.scss";

const PopupCallUs = dynamic(() => import("@components/shared/popupCallus"));
const CustomOfferExitModal = dynamic(
    () => import("@components/shared/customOfferExitModal"),
);

interface UseExitOffersModalArgs {
    exitModalOffer: ListingItem[] | undefined;
    category: DomainInfo["categories"][number] | null | undefined;
    currentCategorySlug: string | undefined;
    buyerPhoneNumber: string | null | undefined;
    routeConfig:
        | {
              config?: { settings?: { hidePageOffers?: boolean } };
          }
        | undefined;
    showFormModal: boolean | undefined;
    isFormInsideOffersModal: boolean;
    singleQuestion: boolean | undefined;
    showFromInExitModal: boolean;
    showExitModal: boolean | undefined;
    showRegulations: boolean;
    disableExitModal?: boolean;
    clicksOffers: ClickOffer[] | null | undefined;
    listRequestId: string;
    visitDetails: { region?: string | null } | null | undefined;
    popupOffers: ListingItem[];
    popupStyle: PopupStyle | undefined;
    offersPopupActive: boolean;
    setOffersPopupActive: (val: boolean) => void;
    setPopupOffers: (offers: ListingItem[]) => void;
    toggleShowFormInExitModal: (val: boolean) => void;
    customOfferExitModal?: boolean;
    customOfferExitModalImage?: string;
    customOfferExitModalContent?: string;
}

export interface UseExitOffersModalReturn {
    handleOffersPopupVisibility: () => void;
    displayExitModal: boolean;
    displayFormInOfferModal: boolean;
    modalOffersContent: React.ReactNode;
    modalOfferClicksContent: React.ReactNode;
    modalWidth: number;
}

export function useExitOffersModal({
    exitModalOffer,
    category,
    currentCategorySlug,
    buyerPhoneNumber,
    routeConfig,
    showFormModal,
    isFormInsideOffersModal,
    singleQuestion,
    showFromInExitModal,
    showExitModal,
    showRegulations,
    clicksOffers,
    listRequestId,
    visitDetails,
    popupOffers,
    popupStyle,
    offersPopupActive,
    setOffersPopupActive,
    setPopupOffers,
    toggleShowFormInExitModal,
    disableExitModal,
    customOfferExitModal,
    customOfferExitModalImage,
    customOfferExitModalContent,
}: UseExitOffersModalArgs): UseExitOffersModalReturn {
    const popupShowCountRef = useRef(0);
    const modalWidthRef = useRef(900);

    const doesTheOfferHaveANumber = (offer: ListingItem | undefined): boolean =>
        !!(
            offer?.proxiedNumber?.phoneNumber ||
            offer?.hiddenAttributes?.phoneNumber ||
            offer?.phoneValue ||
            offer?.attributes?.phoneNumber ||
            buyerPhoneNumber
        );

    const handleOffersPopupVisibility = () => {
        if (disableExitModal) return;
        const offers = exitModalOffer?.filter((offer) => {
            if (
                (!category?.slug &&
                    currentCategorySlug &&
                    offer.category.slug === currentCategorySlug) ||
                (category?.slug && offer.category.slug === category?.slug)
            ) {
                return offer;
            }
        });
        const hideExitModalWithForm: boolean =
            (category?.config?.hideExitModalWithForm as boolean) ?? false;
        const isFirstShow = popupShowCountRef.current === 0;
        const shouldShowOffersPopup =
            !!offers?.length &&
            !offersPopupActive &&
            isFirstShow &&
            !showFromInExitModal;
        const shouldShowPhonePopup = !!buyerPhoneNumber && isFirstShow;

        if (shouldShowOffersPopup || shouldShowPhonePopup) {
            if (offers?.length) {
                setPopupOffers(offers);
            }
            const shouldShowModal =
                !!buyerPhoneNumber ||
                !routeConfig?.config?.settings?.hidePageOffers;

            if (shouldShowModal) {
                setOffersPopupActive(!showFormModal);
                popupShowCountRef.current++;
            }
        } else if (
            !isFormInsideOffersModal &&
            popupShowCountRef.current === 0 &&
            !singleQuestion &&
            !hideExitModalWithForm
        ) {
            toggleShowFormInExitModal(true);
            popupShowCountRef.current++;
            return;
        }
    };

    const displayExitModal = useMemo(() => {
        if (showExitModal) {
            return true;
        }
        return false;
    }, [showExitModal]);

    const displayFormInOfferModal = useMemo(
        () => showFromInExitModal,
        [showFromInExitModal],
    );
    //[todo] should review this use effect i think we can remove it
    useEffect(() => {
        if (displayExitModal || displayFormInOfferModal) {
            handleOffersPopupVisibility();
        }
    }, [displayExitModal, displayFormInOfferModal]);

    const modalOffersContent = useMemo(() => {
        if (
            customOfferExitModal &&
            Array.isArray(popupOffers) &&
            popupOffers.length === 1 &&
            doesTheOfferHaveANumber(popupOffers[0])
        ) {
            return (
                <CustomOfferExitModal
                    image={customOfferExitModalImage}
                    content={customOfferExitModalContent}
                    offer={popupOffers[0]}
                />
            );
        }
        if (
            (Array.isArray(popupOffers) &&
                popupOffers.length === 1 &&
                doesTheOfferHaveANumber(popupOffers[0]) &&
                popupOffers[0]?.features?.length === 0) ||
            doesTheOfferHaveANumber(popupOffers[0])
        ) {
            modalWidthRef.current = 500;
            return (
                <PopupCallUs
                    popupStyle={popupStyle}
                    offerAsProp={popupOffers[0]}
                />
            );
        }
        return popupOffers.map((offer) => (
            <OfferBlock
                zone={affiliateOfferZones.EXIT_MODAL}
                offer={offer}
                key={offer.id}
                className={styles["offer"]}
                displayLinkAndPhoneNumber={doesOfferHaveALink(offer)}
            />
        ));
    }, [
        popupOffers,
        buyerPhoneNumber,
        customOfferExitModal,
        customOfferExitModalImage,
        customOfferExitModalContent,
    ]);

    const modalOfferClicksContent = useMemo(() => {
        if (showRegulations && clicksOffers && clicksOffers.length) {
            return (
                <RegulationsContent
                    title="Hey Californians!"
                    description="Your state restricts extended auto
                                    warranties. You can get complete
                                    coverage by looking into mechanical
                                    breakdown insurance (MBI). Visit these
                                    websites to find out more:"
                >
                    {clicksOffers?.slice(0, 2)?.map((clickOffer, index) => (
                        <InsuranceOffer
                            key={index}
                            index={index}
                            item={{
                                ...clickOffer,
                                slug: `${clickOffer.slug}?id=${listRequestId}`,
                            }}
                            category="Auto Insurance"
                            state={visitDetails?.region ?? undefined}
                            primaryColor="#f79626"
                            textColor="#266199"
                        />
                    ))}
                </RegulationsContent>
            );
        }
        return null;
    }, [clicksOffers]);

    return {
        handleOffersPopupVisibility,
        displayExitModal,
        displayFormInOfferModal,
        modalOffersContent,
        modalOfferClicksContent,
        modalWidth: modalWidthRef.current,
    };
}
